import { Injectable } from '@angular/core';
import { HttpErrorHandler, HandleError } from '@app/shared/services/http-error-handler.service';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { HeadersSetterHelper } from '@app/shared/helpers/http-headers-setter-helper';
import {
  ISpringResponseBody, observeTypes,
  IPaymentDataPostResponse, IPaymentStatusResponse, PaymentStatusCode, IPaymentDataStatusResponse
} from '@app/shared/interfaces/response-interfaces';
import { AppConfig } from '@app/config/app.config';
import { catchError, map, debounce } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { IOrder } from '@app/certificates/history/history';
import { timer } from 'rxjs';
import { IPackRequest } from '@app/shared/interfaces/request-interfaces';
import { IPackItem } from '@app/certificates/packs/packs';
import {
  ActionPacksPaymentSuccess, ActionPacksPaymentFailed,
  ActionPacksPaymentError, ActionPacksMakeOrderSuccess, ActionPacksPaymentUpdated
} from './packs.reducer';
import { LoggerService } from '@app/shared/services/logger.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PackService {
  private handleError: HandleError;
  private headerHelper: HeadersSetterHelper;
  public paymentData = new Subject<any>();

  constructor(
    private httpErrorHandler: HttpErrorHandler,
    private http: HttpClient,
    private store: Store<any>,
    private logger: LoggerService
  ) {
    this.handleError = httpErrorHandler.createHandleError('PackService');
    this.headerHelper = new HeadersSetterHelper(this.store);

  }

  createOrder(req: IPackRequest): Observable<ISpringResponseBody | any> {
    const url = AppConfig.endpoints.order.create;
    return this.http.post<ISpringResponseBody>(url, req, this.headerHelper.postOptions(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('createOrder')),
      map((res: ISpringResponseBody) => {
        // this.logger.log(res);
        return res;
      })
    );
  }

  getPaymentData(orderId: string, orderAmount: string): Observable<IPaymentDataPostResponse> {
    const url = AppConfig.endpoints.order.paymentData;
    const bodyDataParams = { orderId: orderId, orderAmount: orderAmount};
    return this.http.post<IPaymentDataPostResponse>(url, bodyDataParams, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        debounce(() => timer(1000)),
        catchError(this.handleError('getPaymentData')),
        map((res: IPaymentDataPostResponse) => {
          // this.logger.log(res);
          this.paymentData.next(res.data);
          return res;
        })
      );
  }

  getMPIPaymentStatus(orderId: any): Observable<any> {
    const url = AppConfig.endpoints.order.mpiResponseStatus + '/' + orderId;
    return this.http.get<any>(url, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        catchError(this.handleError('getMPIPaymentStatus')),
        map((res: any) => {
          // this.logger.log(res);
          return res;
        })
      );
  }
 
  //TODO: remove this obsolate method and clean the corresponding code and objects.
  getPaymentStatus(orderId: number): Observable<IPaymentStatusResponse> {
    const url = AppConfig.endpoints.order.status + '/' + orderId;
    return this.http.get<IPaymentStatusResponse>(url, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        catchError(this.handleError('getPaymentStatus')),
        map((res: IPaymentStatusResponse) => {
          // this.logger.log(res);
          return res;
        })
      );
  }

  getInvoice(invoiceNumber: string, copyOriginal: string): Observable<any> {
    return this.http.get(
      AppConfig.endpoints.order.invoice + '/' + invoiceNumber + '/' + copyOriginal,
      this.headerHelper.fileOptions(observeTypes.RESPONSE, 'blob')
    ).pipe(
      catchError(this.handleError('getInvoice'))
    );
  }

  getCompanyAsPayerData(identityValue: string, countryCode: string): Observable<any> {
    const url = AppConfig.endpoints.order.companyAsPayer + '/' + identityValue + '/' + countryCode;
    return this.http.get<any>(url, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        catchError(this.handleError('getCompanyAsPayerData')),
        map((res: any) => {
          return res;
        })
      );
  }

  updatePaymentStatus(order: IOrder, response: IPaymentDataStatusResponse) {
    switch (response.data.statusCode) {

      case PaymentStatusCode.FINISHED_SUCCESS:
        this.store.dispatch(new ActionPacksPaymentSuccess(order));
        break;
      case PaymentStatusCode.FINISHED_REJECTED:
        this.store.dispatch(new ActionPacksPaymentFailed(order));
        break;
      case PaymentStatusCode.WAITING_PAYMENT_IN_PROGRESS:
        this.store.dispatch(new ActionPacksPaymentUpdated(order, response.data.statusCode));
        break;
      case PaymentStatusCode.NEW:
        this.store.dispatch(new ActionPacksMakeOrderSuccess(order));
        break;
      case PaymentStatusCode.ERROR:
      default:
        this.store.dispatch(new ActionPacksPaymentError(order, response));
    }

  }

  getPackCountTranslationCode(count: IPackItem['count']): string {
    if (count > -1) {
      return 'packsTexts.packCount';
    } else {
      return 'packsTexts.unlimitedCnt';
    }
  }
}
